import React, { useState } from 'react';
import moment from 'moment-timezone';
import _ from 'underscore';
import classnames from 'classnames';
import { Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import SessionStore from 'stores/SessionStore';
import EditButton from 'components/v2/buttons/EditButton';
import ApproveButton from 'components/v2/buttons/ApproveButton';
import { getShipmentTimezone } from 'helpers/shipments';
import { updateHold } from 'utils/ShipmentWebAPIUtils';
import ShipmentOnHoldModal from './ShipmentOnHoldModal';
import BannerBrand from './BannerBrand';
const isAdmin = SessionStore.isAdmin();
const getTimezone = (zone) => {
    return moment.tz(zone).zoneAbbr();
};
const formatDate = (date, timezone) => {
    const DATE_FORMAT = 'L LT';
    return `${moment(date).tz(timezone).format(DATE_FORMAT)} ${getTimezone(timezone) || ''}`;
};
const BannerItem = (props) => {
    return (React.createElement("div", { className: `flex flex-col ${props.containerClassName}` },
        React.createElement("div", { className: "flex" },
            React.createElement("label", { className: `form-label ${props.labelClassName}` }, props.label),
            props.showIcon && React.createElement("i", { className: `fa ${props.iconClassName}` })),
        React.createElement("div", null,
            React.createElement("span", { className: props.valueClassName }, props.value))));
};
const ShipmentHold = (props) => {
    const { hold, timezone, onClick, onApprove, t } = props;
    const containerClassNames = classnames({
        flex: true,
        'space-x-8': false,
        'w-full': true,
        'p-4': true,
        'bg-blue-600': true,
        'bg-opacity-60': !_.isEmpty(hold.resolved_at),
        'bg-opacity-90': _.isEmpty(hold.resolved_at),
        'hover:bg-opacity-100': true,
        'hover:bg-opacity-80': !_.isEmpty(hold.resolved_at),
        'cursor-pointer': true,
        'hover:text-white': true,
    });
    const iconContainerClassName = classnames({
        'bg-blue-800': true,
        'bg-opacity-50': !_.isEmpty(hold.resolved_at),
    });
    function handleApprove() {
        let approved = hold;
        approved.resolved_at = moment().tz(timezone).toISOString();
        onApprove(approved);
    }
    const description = hold.rawType ? hold.rawType : hold.remarks;
    return (React.createElement("div", { className: containerClassNames },
        React.createElement("div", { className: "w-1/8 min-w-16" },
            React.createElement(BannerBrand, { icon: "ferry", iconClassName: "text-white", iconContainerClassName: iconContainerClassName, textClassName: "text-white" })),
        React.createElement("div", { className: "w-3/4 pl-8 max-w-md min-w-16" },
            React.createElement(BannerItem, { containerClassName: "text-white", label: t("TYPE", { count: 1 }), labelClassName: "text-white", value: hold.type, valueClassName: "text-xs" })),
        React.createElement("div", { className: "w-3/4 pl-8 max-w-md min-w-16" },
            React.createElement(BannerItem, { containerClassName: "text-white", label: t("DESCRIPTION", { count: 1 }), labelClassName: "text-white", value: `${description || ''}`, valueClassName: "text-xs" })),
        React.createElement("div", { className: "w-3/4 pl-8 max-w-md min-w-16", style: { minWidth: 90 } },
            React.createElement(BannerItem, { containerClassName: "text-white", label: t('REPORTED'), labelClassName: "text-white", value: hold.reported_at ? formatDate(hold.reported_at, timezone) : '', valueClassName: "italic text-xs" })),
        React.createElement("div", { className: "w-3/4 pl-8" },
            React.createElement(BannerItem, { containerClassName: "text-white min-w-200", label: t('RESOLVED'), iconClassName: "fa-check text-white px-1 hover:text-green", showIcon: !_.isEmpty(hold.resolved_at), labelClassName: "text-white", value: hold.resolved_at
                    ? formatDate(hold.resolved_at, timezone)
                    : 'Pending', valueClassName: "italic text-xs" })),
        React.createElement("div", { className: "w-full flex flex-wrap content-center justify-end max-w-xs space-x-1 min-w-32" },
            isAdmin && React.createElement(EditButton, { color: "blue", onClick: () => onClick(hold) }),
            isAdmin && (React.createElement(ApproveButton, { disabled: hold.resolved_at, onClick: () => handleApprove() })))));
};
const ShipmentHoldSummary = (props) => {
    const { holds, timezone, onClick, isOpen, t } = props;
    const hold = holds[0]; // @TODO parse out latest hold
    const containerClassNames = classnames({
        flex: true,
        'space-x-8': false,
        'w-full': true,
        'px-4': true,
        'py-6': true,
        rounded: true,
        'bg-blue-600': true,
        'bg-opacity-90': true,
        'hover:bg-opacity-100': true,
        'cursor-pointer': true,
        'text-gray-100': true,
        'hover:text-white': true,
    });
    const chevronClass = classnames({
        fa: true,
        'fa-chevron-right': isOpen === false,
        'fa-chevron-down': isOpen === true,
    });
    const resolved = holds.filter((hold) => {
        return !_.isEmpty(hold.resolved_at);
    });
    const pending = holds.filter((hold) => {
        return _.isEmpty(hold.resolved_at);
    });
    return (React.createElement("div", { className: `flex ${containerClassNames}`, onClick: onClick },
        React.createElement("div", { className: "w-1/8 min-w-16" },
            React.createElement(BannerBrand, { icon: "ferry", iconClassName: "text-white", iconContainerClassName: "bg-blue-800 hover:bg-blue-700", textClassName: "text-white" })),
        React.createElement("div", { className: "w-3/4 pl-8 max-w-md min-w-16" },
            React.createElement(BannerItem, { containerClassName: "text-white", label: t('PENDING_HOLDS'), labelClassName: "text-white", value: pending.length, valueClassName: "text-xs" })),
        React.createElement("div", { className: "w-3/4 pl-8 max-w-md min-w-16" },
            React.createElement(BannerItem, { containerClassName: "text-white", label: t('RESOLVED_HOLDS'), labelClassName: "text-white", value: resolved.length, valueClassName: "text-xs" })),
        React.createElement("div", { className: "w-3/4 pl-8 max-w-md min-w-16" },
            React.createElement(BannerItem, { containerClassName: "text-white", label: t('TOTAL_HOLDS'), labelClassName: "text-white", value: holds.length, valueClassName: "text-xs" })),
        React.createElement("div", { className: "w-3/4 pl-8", style: { minWidth: 90 } },
            React.createElement(BannerItem, { containerClassName: "text-white", label: t('LAST_UPDATE'), labelClassName: "text-white", value: hold.reported_at ? formatDate(hold.reported_at, timezone) : '', valueClassName: "text-xs" })),
        React.createElement("div", { className: "w-full flex flex-wrap content-center justify-end max-w-xs" },
            React.createElement("i", { className: chevronClass }))));
};
const ShipmentHolds = (props) => {
    const { shipment } = props;
    if (!shipment.holds || !shipment.holds.length) {
        return null;
    }
    const [holds, setHolds] = useState(_.sortBy(shipment.holds, 'resolved_at').reverse());
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [activeHold, setActiveHold] = useState({});
    const [showHoldModal, setShowHoldModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const onHoldClick = (hold) => {
        setActiveHold(hold);
        setShowHoldModal(true);
    };
    const onHoldChange = (field, value) => {
        activeHold[field] = value;
        setActiveHold(activeHold);
    };
    const onSaveHold = async (hold) => {
        setIsLoading(true);
        const response = await updateHold(shipment._id, hold);
        if (response) {
            setShowHoldModal(false);
            setHolds(_.sortBy(response.holds, 'resolved_at').reverse());
        }
        setIsLoading(false);
    };
    const timezone = getShipmentTimezone(shipment);
    return (React.createElement("div", { className: "space-y-2" },
        React.createElement(ShipmentHoldSummary, { t: t, holds: holds, timezone: timezone, isOpen: isOpen, onClick: () => setIsOpen(!isOpen) }),
        React.createElement(Transition, { show: isOpen, enter: "transition ease-out duration-350", enterFrom: "opacity-0 translate-y-1", enterTo: "opacity-100 translate-y-0", leave: "transition ease-in duration-350", leaveFrom: "opacity-100 translate-y-0", leaveTo: "opacity-0 translate-y-1" },
            React.createElement("div", { className: "max-h-80 overflow-y-scroll pb-4 space-y-2 text-white transition duration-5000 ease-in-out transform" }, holds.map((hold) => {
                return (React.createElement(ShipmentHold, { t: t, hold: hold, timezone: timezone, onClick: onHoldClick, onApprove: (approved) => onSaveHold(approved) }));
            }))),
        showHoldModal && (React.createElement(ShipmentOnHoldModal, { hold: activeHold, setShowHoldModal: setShowHoldModal, onSave: (hold) => onSaveHold(hold), timezone: timezone, onHoldChange: onHoldChange, loading: isLoading }))));
};
export default ShipmentHolds;
